import { useQuery } from '@tanstack/react-query';
import {
	Grid3x2Icon,
	LogOutIcon,
	MapIcon,
	MoonIcon,
	SettingsIcon,
	SunIcon,
	UserIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchRoutes, useLocation, useNavigate } from 'react-router';
import { MobileAssetNav } from './assets/MobileAssetNav';
import { ROLE } from '../common/role';
import { useAppContext } from '../context/AppContext';
import { useKeycloak } from '../context/KeycloakContext';
import { useTheme } from '../context/ThemeContext';
import { useMobileAssetTab } from '../context/MobileAssetTabContext';
import { getProfileOptions } from '../queries/users/profile';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Restricted } from './Restricted';
import { SubAssetBreadcrumb } from './sub-asset/sub-asset-selector/SubAssetBreadcrumb';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from './ui/dropdown-menu';

export function Header() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { data: user } = useQuery(getProfileOptions());
	const { onLogout } = useAppContext();
	const { keycloak } = useKeycloak();
	const { toggleTheme } = useTheme();
	const { activeTab, setActiveTab, showMapTab } = useMobileAssetTab();

	const { isAssetRoute, isHomeRoute, isNewAsset, isAdministrationRoute } = useMemo(() => {
		const isHomeRoute = location.pathname === '/';
		const assetMatchedRoutes = matchRoutes([{ path: '/assets/:assetId/*' }], location);
		const isAssetRoute = !!assetMatchedRoutes?.length;
		const isNewAsset = assetMatchedRoutes?.[0]?.params?.assetId === 'new';
		const isAdministrationRoute = location.pathname === '/administration';
		return {
			isHomeRoute,
			isAssetRoute,
			isNewAsset,
			isAdministrationRoute,
		};
	}, [location]);

	const assetBreadcrumb = useMemo(() => {
		if (isAssetRoute) {
			return isNewAsset ? t('assets.nav.newAsset') : <SubAssetBreadcrumb />;
		}
		return '';
	}, [isAssetRoute, isNewAsset, t]);

	const title = useMemo(() => {
		if (isAssetRoute) {
			return assetBreadcrumb;
		}
		if (isAdministrationRoute) {
			return t('common.actions.administration');
		}
		return '';
	}, [isAdministrationRoute, isAssetRoute, assetBreadcrumb, t]);

	const organization = useMemo(() => {
		const org = keycloak?.tokenParsed?.organization;
		if (typeof org === 'string') {
			return org;
		}
		return Object.keys(org || {})[0] || '';
	}, [keycloak?.tokenParsed?.organization]);

	const themeButton = (
		<>
			<LanguageSwitcher />
			<Button
				variant="ghost"
				size="icon"
				onClick={toggleTheme}
				className="relative h-9 w-9 rounded-full bg-muted/30 border border-border p-0 shadow-sm hover:bg-muted/50 transition-colors"
				title={t('common.theme.toggle')}
			>
				<SunIcon className="h-[18px] w-[18px] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0 text-muted-foreground" />
				<MoonIcon className="absolute h-[18px] w-[18px] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100 text-muted-foreground" />
				<span className="sr-only">{t('common.theme.toggle')}</span>
			</Button>
		</>
	);

	const userDropdown = (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative rounded-full border p-0 h-9 w-9 bg-muted/30 border-border shadow-sm hover:bg-muted/50 transition-colors"
				>
					<UserIcon className="size-5" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-56 border" align="end" forceMount>
				<DropdownMenuLabel className="font-normal">
					<div className="flex flex-col space-y-1">
						<p className="text-sm font-medium leading-none text-foreground">{user?.data?.name}</p>
						<p className="text-xs leading-none text-muted-foreground">{organization}</p>
					</div>
				</DropdownMenuLabel>
				<DropdownMenuSeparator className="bg-accent" />
				<Restricted some to={[ROLE.ORGANIZATION_READ, ROLE.USERS_READ]}>
					<DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/administration')}>
						<SettingsIcon className="mr-2 h-4 w-4" />
						<span>{t('common.actions.administration')}</span>
					</DropdownMenuItem>
				</Restricted>
				<DropdownMenuItem className="cursor-pointer" onClick={onLogout} variant="destructive">
					<LogOutIcon className="mr-2 h-4 w-4" />
					<span>{t('common.actions.logout')}</span>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);

	return (
		<header className="flex-none border-b bg-card sticky top-0 z-30 text-foreground">
			{/* ── MOBILE layout (< lg) ── */}
			<div className="lg:hidden h-16 flex items-center px-4 gap-3">
				{isAssetRoute && !isNewAsset ? (
					<>
						{/* Left: Hamburger menu */}
						<MobileAssetNav />

						{/* Center: Asset Info / Map switcher — only on Cesium routes */}
						<div className="flex-1 flex justify-center">
							{showMapTab && (
								<div className="flex bg-muted p-1 border border-border rounded-lg shrink-0 shadow-inner gap-0.5">
									<button
										type="button"
										onClick={() => setActiveTab('info')}
										className={cn(
											'h-8 px-3 rounded-md flex items-center transition-all',
											activeTab === 'info'
												? 'bg-primary text-primary-foreground shadow-md'
												: 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
										)}
										title={t('assets.detail.infoTab')}
									>
										<Grid3x2Icon className="h-4 w-4" />
									</button>
									<button
										type="button"
										onClick={() => setActiveTab('map')}
										className={cn(
											'h-8 px-3 rounded-md flex items-center transition-all',
											activeTab === 'map'
												? 'bg-primary text-primary-foreground shadow-md'
												: 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
										)}
										title={t('assets.detail.mapTab')}
									>
										<MapIcon className="h-4 w-4" />
									</button>
								</div>
							)}
						</div>

						{/* Right: Theme + User */}
						<div className="flex items-center gap-2">
							{themeButton}
							{userDropdown}
						</div>
					</>
				) : (
					<>
						{/* Left: Logo */}
						{/* biome-ignore lint/a11y/useKeyWithClickEvents: need for click */}
						<img
							src="/logo.png"
							alt={t('assets.shell.logoAlt')}
							className="h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity"
							onClick={() => navigate('/')}
						/>
						<div className="flex-1">
							{title && <h1 className="text-sm font-semibold text-foreground">{title}</h1>}
						</div>
						<div className="flex items-center gap-2">
							{themeButton}
							{userDropdown}
						</div>
					</>
				)}
			</div>

			{/* ── DESKTOP layout (lg+) ── */}
			<div className="hidden lg:flex h-16 px-4 items-center justify-between">
				<div className="flex items-center gap-4">
					{isHomeRoute && (
						// biome-ignore lint/a11y/useKeyWithClickEvents: need for click
						<img
							src="/logo_with_text_cropped.png"
							alt={t('assets.shell.logoAlt')}
							className="w-30 cursor-pointer"
							onClick={() => navigate('/')}
						/>
					)}
					{!isHomeRoute && (
						// biome-ignore lint/a11y/useKeyWithClickEvents: need for click
						<img
							src="/logo.png"
							alt={t('assets.shell.logoAlt')}
							className="w-8 cursor-pointer"
							onClick={() => navigate('/')}
						/>
					)}
					<div className="hidden md:flex items-center gap-2">
						<h1 className="text-xl font-semibold text-foreground">{title}</h1>
					</div>
				</div>
				<div className="flex items-center gap-4">
					{themeButton}
					{userDropdown}
				</div>
			</div>
		</header>
	);
}
